import type {
  CreateOrUpdateEmissionTargetRequest,
  EmissionTarget,
  EmissionTargetLevel,
  EmissionTargetType,
} from "api/emission-targets";
import type { EditTargetData, TargetFormData, TargetLevel, TargetType } from "./types";
import {
  ANNUAL_PATH_OPTIONS,
  TARGET_TYPES,
} from "./types";

export function getYearsBetween(baselineYear: number, targetYear: number): number[] {
  const years: number[] = [];
  for (let y = baselineYear; y <= targetYear; y += 1) {
    years.push(y);
  }
  return years;
}

export function computeEvenAnnualReduction(
  targetType: string,
  baselineYear: number,
  targetYear: number,
  targetValue: number
): string {
  const numYears = targetYear - baselineYear;
  if (numYears <= 0) return "—";
  if (targetType === TARGET_TYPES.PERCENT_REDUCTION) {
    const perYear = targetValue / numYears;
    return `${perYear.toFixed(1)}%`;
  }
  const perYear = targetValue / numYears;
  return `${perYear.toFixed(1)} tCO₂e`;
}

const LEVEL_TO_API: Record<TargetLevel, EmissionTargetLevel> = {
  Corporate: "GLOBAL",
  "Business Unit": "BUSINESS_UNIT",
  Site: "SITE",
};

const TARGET_TYPE_TO_API: Record<string, EmissionTargetType> = {
  [TARGET_TYPES.PERCENT_REDUCTION]: "PERCENTAGE",
  [TARGET_TYPES.ABSOLUTE_TCO2E]: "ABSOLUTE",
};

const ANNUAL_PATH_TO_API = {
  [ANNUAL_PATH_OPTIONS.EVEN]: "EVEN" as const,
  [ANNUAL_PATH_OPTIONS.CUSTOM]: "CUSTOM" as const,
};

/**
 * Build API request body from form data.
 * entity is taken from form.entity (dropdown value = entity string for API).
 * When level is Corporate (GLOBAL), entity is disabled and sent as empty string.
 */
export function formDataToRequest(
  data: TargetFormData
): CreateOrUpdateEmissionTargetRequest | null {
  const isCorporate = data.targetLevel === "Corporate";
  const entity = isCorporate ? "" : (data.entity?.trim() ?? "");
  if (!isCorporate && !entity) {
    return null;
  }

  const baseYear = data.baselineYear
    ? parseInt(String(data.baselineYear), 10)
    : 0;
  const targetYear = data.targetYear
    ? parseInt(String(data.targetYear), 10)
    : 0;
  const value = parseFloat(data.targetValue);
  if (!data.targetLevel || !baseYear || !targetYear || !Number.isFinite(value)) {
    return null;
  }

  const request: CreateOrUpdateEmissionTargetRequest = {
    level: LEVEL_TO_API[data.targetLevel as TargetLevel],
    entity,
    targetType: TARGET_TYPE_TO_API[data.targetType] ?? "ABSOLUTE",
    baseYear,
    targetYear,
    value,
    annual_target_path: ANNUAL_PATH_TO_API[data.annualTargetPath],
    scope: data.emissionsScope || "scope1",
    notes: data.notes?.trim() || null,
  };

  if (data.annualTargetPath === ANNUAL_PATH_OPTIONS.CUSTOM && data.customAnnualValues) {
    request.customYearTarget = Object.entries(data.customAnnualValues)
      .filter(([, v]) => v !== "" && v != null)
      .map(([year, val]) => ({
        year: parseInt(year, 10),
        value: parseFloat(String(val)),
      }))
      .filter(({ year, value: v }) => Number.isInteger(year) && Number.isFinite(v));
  }

  return request;
}

const API_LEVEL_TO_UI: Record<EmissionTargetLevel, TargetLevel> = {
  GLOBAL: "Corporate",
  BUSINESS_UNIT: "Business Unit",
  SITE: "Site",
};

const API_TARGET_TYPE_TO_UI: Record<string, string> = {
  ABSOLUTE: TARGET_TYPES.ABSOLUTE_TCO2E,
  PERCENTAGE: TARGET_TYPES.PERCENT_REDUCTION,
  PERCENT_REDUCTION: TARGET_TYPES.PERCENT_REDUCTION, // backward compatibility
};

const API_ANNUAL_PATH_TO_UI = {
  EVEN: ANNUAL_PATH_OPTIONS.EVEN,
  CUSTOM: ANNUAL_PATH_OPTIONS.CUSTOM,
};

/** Map API emission target to EditTargetData for the modal */
export function emissionTargetToEditTargetData(
  target: EmissionTarget
): EditTargetData {
  return {
    id: target.id,
    targetLevel: API_LEVEL_TO_UI[target.level],
    entity: target.entity,
    targetType: API_TARGET_TYPE_TO_UI[target.targetType] as TargetType,
    emissionsScope: target.scope,
    baselineYear: target.baseYear,
    targetYear: target.targetYear,
    targetValue: target.value,
    annualTargetPath: API_ANNUAL_PATH_TO_UI[target.annual_target_path],
    customAnnualValues: target.yearlyDetails?.length
      ? Object.fromEntries(
        target.yearlyDetails.map((d) => [d.year, d.value])
      )
      : undefined,
    notes: target.notes ?? undefined,
  };
}
